import { createItem, deleteItem, listItems, updateItem } from './sharepoint';

const LIST = 'salesTrackerEvents';

/**
 * Sales Tracker — an append-only log of coverage-hour changes on Parts.
 *
 * Every time a Part's HoursOfCoverage changes (create, or edit that moves
 * the value up or down), Parts.jsx / PartDetail.jsx / SupplierDetail.jsx
 * call logPartCoverageEvent() after the SharePoint write succeeds. The
 * Sales page then reads the log with listSalesTrackerEvents() to render:
 *   - headline totals (sum of HoursAdded, sum of HoursLost)
 *   - a row-per-event table with Date / Part / Plant / +hrs / -hrs
 *
 * Events are never edited or deleted by the app. If Jillian needs to
 * correct a bad entry she'd do it directly in SharePoint.
 */

// List all events, newest first. SharePoint's Created is set server-side,
// so sorting on it works even for rows written before we started stamping
// PostedAt (there shouldn't be any, but the defensiveness matches how
// agreements.js handles the same problem).
export async function listSalesTrackerEvents(opts) {
  return listItems(LIST, {
    orderby: 'fields/Created desc',
    ...opts,
  });
}

/**
 * Record a change to a supplier's Plant Locations & Coverage row.
 * Writes into the same SalesTrackerEvents list as part-coverage events,
 * with Category='Plant Assignment' so the Supplier detail page's
 * Coverage Activity table can filter for these specifically. The Sales
 * Tracker on the Sales page also picks these up — but only the ones
 * that actually moved hours (HoursAdded > 0 or HoursLost > 0) — so
 * rate-only or status-only edits stay confined to the supplier page
 * while genuine hours-up/hours-down events feed the company-wide
 * tracker.
 *
 * @param {Object} event
 * @param {string} event.supplierCode  — e.g. 'AUTOSYS'
 * @param {string} [event.supplierName] — denormalized for the Sales tracker
 * @param {string} event.plantCode     — e.g. 'SHAP'
 * @param {'Added'|'Revised'|'Removed'} event.eventType
 * @param {string} event.summary       — human-readable description of the change
 * @param {number} [event.previousHours] — old hoursAllocated (0 on Added)
 * @param {number} [event.newHours]      — new hoursAllocated (0 on Removed)
 * @param {string} [event.previousStatus] — old coverageStatus ('' on Added)
 * @param {string} [event.newStatus]      — new coverageStatus ('' on Removed)
 * @param {string} [event.hoursUnit]   — e.g. "Per Shift" — recorded for context
 * @param {string} [event.postedBy]    — user display name or email
 *
 * Sales-Tracker math (HoursAdded / HoursLost): only Active hours count.
 * Effective previous hours = previousHours when previousStatus was 'Active',
 * otherwise 0. Same rule for new. Delta is the difference of those two
 * effective values, so:
 *   - Prospecting (8h) → Active (8h)  ⇒  +8 added (status flipped on)
 *   - Active (8h)      → Closed (8h)  ⇒  -8 lost  (status flipped off)
 *   - Active (8h)      → Active (10h) ⇒  +2 added (hours up while Active)
 *   - Prospecting (5h) → Prospecting (8h) ⇒ 0 (never counted)
 *   - Removed Active (8h)             ⇒  -8 lost
 *   - Added new pair as Active (8h)   ⇒  +8 added
 * PreviousHours / NewHours columns still record the raw hoursAllocated
 * values so the Coverage Activity audit log on the Supplier detail page
 * shows the actual transition.
 */
export async function logSupplierPlantAssignmentEvent(event) {
  const previous = Number(event.previousHours) || 0;
  const next = Number(event.newHours) || 0;
  const wasActive = (event.previousStatus || '') === 'Active';
  const nowActive = (event.newStatus || '') === 'Active';
  const effPrev = wasActive ? previous : 0;
  const effNext = nowActive ? next : 0;
  const delta = effNext - effPrev;
  const hoursAdded = delta > 0 ? delta : 0;
  const hoursLost = delta < 0 ? Math.abs(delta) : 0;

  // Title is the canonical short label SharePoint uses in its native list
  // view ("AUTOSYS / SHAP · Revised +2h"). Not consumed by the app but
  // makes eyeballing the raw list friendlier. Uses the same effective
  // delta so the title matches what the Sales Tracker counts.
  const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
  const deltaLabel = delta !== 0 ? ` ${sign}${Math.abs(delta)}h` : '';
  const title = [
    event.supplierCode || '',
    event.plantCode ? `/ ${event.plantCode}` : '',
    event.eventType ? `· ${event.eventType}${deltaLabel}` : '',
  ]
    .filter(Boolean)
    .join(' ');
  return createItem(LIST, {
    Title: title || 'Plant Assignment',
    Category: 'Plant Assignment',
    SupplierCode: event.supplierCode || '',
    // Reuse the PartTitle column to carry the supplier display name —
    // the Plant Assignment category never has a part attached, so the
    // column is otherwise unused for these rows. This keeps the
    // Sales Tracker query to a single list read with a denormalized
    // label already in hand.
    PartTitle: event.supplierName || '',
    PlantCode: event.plantCode || '',
    EventType: event.eventType,
    Summary: event.summary || '',
    PostedBy: event.postedBy || '',
    PostedAt: new Date().toISOString(),
    // Part-coverage columns blank for plant-assignment events. Hours
    // columns reflect the hoursAllocated delta when one was passed.
    PartCode: '',
    HoursAdded: hoursAdded,
    HoursLost: hoursLost,
    PreviousHours: previous,
    NewHours: next,
  });
}

/**
 * List every Plant Assignment event for one supplier, newest first.
 * The filter runs client-side — the list stays small enough that a
 * full read is effectively free and avoids Graph's non-indexed-column
 * filter landmines (same rationale as deletePartCoverageEvents).
 */
export async function listSupplierActivityEvents(supplierCode) {
  const code = (supplierCode || '').trim().toUpperCase();
  if (!code) return [];
  const rows = await listItems(LIST, { orderby: 'fields/Created desc' });
  return rows.filter(
    (r) =>
      (r.Category || '') === 'Plant Assignment' &&
      String(r.SupplierCode || '').trim().toUpperCase() === code,
  );
}

/**
 * Log a new contact being added to a Plant or Supplier. Consumed by the
 * Dashboard's "Contact Changes" column so Directors see a fresh list of
 * who joined which plant/supplier contact roster recently.
 *
 * @param {Object} event
 * @param {'Plant'|'Supplier'} event.parentType
 * @param {string} event.parentCode    — PlantCode or SupplierCode
 * @param {string} [event.parentName]  — parent display name for the feed
 * @param {string} event.contactName   — the newly-added contact's name
 * @param {string} [event.contactTitle]
 * @param {string} [event.postedBy]
 */
export async function logContactAddedEvent(event) {
  const parentLabel = event.parentName || event.parentCode || '';
  const contactLabel = event.contactTitle
    ? `${event.contactName} (${event.contactTitle})`
    : event.contactName || 'Unnamed contact';
  const summary = `Added ${contactLabel} to ${parentLabel}`.trim();
  const title =
    `${event.parentType || 'Contact'} · ${parentLabel} · +${event.contactName || ''}`.trim();
  return createItem(LIST, {
    Title: title,
    Category: 'Contact',
    EventType: 'Added',
    // Route the parent code to the column that matches its type so the
    // Supplier detail and Plant detail pages can both query by their
    // natural key without re-indexing.
    SupplierCode: event.parentType === 'Supplier' ? event.parentCode || '' : '',
    PlantCode: event.parentType === 'Plant' ? event.parentCode || '' : '',
    // Denormalized parent name for the Dashboard feed. PartTitle column
    // already exists and we're not using it for contact events, so reuse
    // it as the human-readable parent label (saves adding another column).
    PartTitle: parentLabel,
    // Denormalized contact identity for the feed; we don't have a
    // dedicated ContactName column, so stash it in PartCode (unused for
    // contact events). Keeps the Dashboard query to a single list read.
    PartCode: event.contactName || '',
    Summary: summary,
    PostedBy: event.postedBy || '',
    PostedAt: new Date().toISOString(),
    HoursAdded: 0,
    HoursLost: 0,
    PreviousHours: 0,
    NewHours: 0,
  });
}

/**
 * Fetch the most recent contact-added events across all plants and
 * suppliers, newest first. Caller can cap the list with `limit`. Runs a
 * single list read and filters client-side — same rationale as the other
 * tracker queries.
 */
export async function listRecentContactEvents({ limit = 10 } = {}) {
  const rows = await listItems(LIST, { orderby: 'fields/Created desc' });
  const contactRows = rows.filter((r) => (r.Category || '') === 'Contact');
  return limit > 0 ? contactRows.slice(0, limit) : contactRows;
}

/**
 * Record a coverage-hour change on a Part.
 *
 * @param {Object} event
 * @param {string} event.partCode   — Part's short code (e.g. "RAMBOX").
 * @param {string} event.partTitle  — Human-readable name (for display).
 * @param {string} [event.plantCode] — Comma-joined plant codes the part ships to.
 * @param {'Added'|'Revised'} event.eventType
 * @param {number} event.previousHours — 0 on 'Added'.
 * @param {number} event.newHours       — the part's HoursOfCoverage after the save.
 * @param {string} [event.postedBy]     — user display name or email.
 *
 * HoursAdded / HoursLost are computed from the previous→new delta so the
 * caller never has to think about which column to populate.
 */
export async function logPartCoverageEvent(event) {
  const previous = Number(event.previousHours) || 0;
  const next = Number(event.newHours) || 0;
  const delta = next - previous;
  // Guard: a "revised" event where nothing actually changed would just
  // pollute the log. Caller should skip us in that case, but double-check
  // so we never write an all-zero row by accident.
  if (event.eventType === 'Revised' && delta === 0) return null;

  const hoursAdded = delta > 0 ? delta : 0;
  const hoursLost = delta < 0 ? Math.abs(delta) : 0;

  // SharePoint requires a Title on every list item — use a summary like
  // "RAMBOX: +2h" so the native list view reads as an event log.
  const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
  const abs = Math.abs(delta);
  const title = event.partCode
    ? `${event.partCode}: ${sign}${abs}h`
    : `${event.eventType} ${sign}${abs}h`;

  return createItem(LIST, {
    Title: title,
    PartCode: event.partCode || '',
    PartTitle: event.partTitle || '',
    PlantCode: event.plantCode || '',
    EventType: event.eventType,
    HoursAdded: hoursAdded,
    HoursLost: hoursLost,
    PreviousHours: previous,
    NewHours: next,
    PostedBy: event.postedBy || '',
    PostedAt: new Date().toISOString(),
  });
}

/**
 * Remove every tracker event tied to a given PartCode. Called from
 * PartDetail.onDelete so a part that was added by mistake doesn't leave
 * a ghost in the Sales Tracker — Jillian's rule is that "Closed" is for
 * real end-of-service (history preserved), and "Delete" is for mistakes
 * (history wiped).
 *
 * Returns the number of rows removed so the caller can log it. Failures
 * on individual deletes are swallowed — the part deletion is the
 * primary operation and we don't want a stuck event row to block it.
 */
/**
 * Patch a single tracker event. Used by the Activity table on
 * PartDetail, where a Director can annotate a coverage change with
 * free-form Notes. We deliberately only expose a narrow patch surface
 * so callers can't accidentally rewrite the audit columns (HoursAdded,
 * PostedAt, etc.) — only Notes is pass-through today.
 */
export async function updateSalesTrackerEvent(id, fields) {
  return updateItem(LIST, id, fields);
}

/**
 * Delete a single tracker event by id. Thin wrapper around the
 * SharePoint delete — provided so callers don't have to import the
 * generic deleteItem and remember the list key. Returns the same
 * { id, deleted } envelope as the SharePoint helper.
 */
export async function deleteSalesTrackerEvent(id) {
  return deleteItem(LIST, id);
}

export async function deletePartCoverageEvents(partCode) {
  const code = (partCode || '').trim();
  if (!code) return 0;
  // Fetch all events and match client-side rather than relying on a
  // SharePoint $filter. Graph's non-indexed-column filter path is the
  // "MayFailRandomly" route — in at least one live delete it came back
  // empty and left a ghost event behind. The tracker list stays small
  // (one row per coverage change), so a full read is effectively free
  // and deterministic.
  const rows = await listItems(LIST);
  const matches = rows.filter(
    (r) => (r.PartCode || '').trim() === code,
  );
  let removed = 0;
  for (const r of matches) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await deleteItem(LIST, r.id);
      removed += 1;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[salesTracker] Failed to delete event', r.id, err);
    }
  }
  return removed;
}
